import React, { useState, useEffect } from 'react';
import { Clip, EmotionMood, StemSet, SoulEntry, EntryTake } from './types';
import { fetchEntry } from './services/entryService';
import { syncTempVaultToUser } from './services/curatorService';
import { checkCurrentSession } from './services/vaultService';
import { Header } from './components/common/Header';
import { ClipCurator } from './components/curator/ClipCurator';
import { SoulStudio } from './components/studio/SoulStudio';
import { SoulClipItem } from './components/studio/studioUtils';
import { SoulShareGallery } from './components/share/SoulShareGallery';
import { SoulSeparatorModal } from './components/separator/SoulSeparatorModal';
import { Inspect } from './pages/Inspect';
import { TokenProvider } from './context/TokenContext';

export const App: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<'capture' | 'sync' | 'share'>('capture');
  const [isInspectMode, setIsInspectMode] = useState<boolean>(false);
  const [currentMood, setCurrentMood] = useState<EmotionMood>('Heroic');
  const [currentClip, setCurrentClip] = useState<Clip | null>(null);
  const [pendingShareClip, setPendingShareClip] = useState<Clip | SoulClipItem | null>(null);
  const [activeEntry, setActiveEntry] = useState<SoulEntry | null>(null);
  const [stems, setStems] = useState<StemSet | null>(null);
  const [isSeparatorModalOpen, setIsSeparatorModalOpen] = useState(false);

  const [userName, setUserName] = useState<string>(() => {
    return localStorage.getItem('soulsync_performer_name') || '';
  });

  // Cryptographic session bootstrap on load (signed HttpOnly cookie check)
  useEffect(() => {
    checkCurrentSession().then((authenticatedUser) => {
      if (authenticatedUser?.username) {
        setUserName(authenticatedUser.username);
        localStorage.setItem('soulsync_performer_name', authenticatedUser.username);
      }
    });
  }, []);

  const handleUserLogin = async (name: string) => {
    setUserName(name);
    if (name) {
      localStorage.setItem('soulsync_performer_name', name);
      // Automatically migrate any clips captured in the Temp Vault to this user's persistent vault
      try {
        await syncTempVaultToUser(name);
      } catch (err) {
        console.warn('Sync temp vault notice:', err);
      }
    } else {
      localStorage.removeItem('soulsync_performer_name');
    }
  };

  // Check URL pathname for inspect route
  useEffect(() => {
    const checkPathname = () => {
      setIsInspectMode(window.location.pathname === '/inspect');
    };
    checkPathname();
    window.addEventListener('popstate', checkPathname);
    return () => window.removeEventListener('popstate', checkPathname);
  }, []);

  // Check URL hash or query params for shared SoulSync entries (#entry=...)
  useEffect(() => {
    const handleHashChange = async () => {
      const hash = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);

      // Check SoulSync Entry deep link
      const entryId = hash.startsWith('#entry=')
        ? hash.replace('#entry=', '')
        : urlParams.get('entry');

      if (entryId) {
        const found = await fetchEntry(entryId);
        if (found) {
          setActiveEntry(found);
          const clipFromEntry: Clip = {
            id: found.clipId,
            title: found.title,
            sourceUrl: found.videoUrl,
            videoUrl: found.videoUrl,
            duration: found.duration,
            trimStart: found.trimStart,
            trimEnd: found.trimEnd,
            posterUrl: found.posterUrl
          };
          setCurrentClip(clipFromEntry);
          setCurrentMood(found.moodTag);
          setCurrentTab('sync');
        }
      }

      // Check SoulClip deep link (#clip=...)
      const clipId = hash.startsWith('#clip=')
        ? hash.replace('#clip=', '')
        : urlParams.get('clip');

      if (clipId) {
        fetch(`/api/curator/clip?id=${encodeURIComponent(clipId)}`)
            .then((res) => res.json())
            .then((data: any) => {
              if (data.success && data.clip) {
                const c = data.clip;
                const clipObj: Clip = {
                  id: c.id,
                  title: c.title,
                  sourceUrl: c.video_url || c.videoUrl,
                  videoUrl: c.video_url || c.videoUrl,
                  audioUrl: c.audio_url || c.audioUrl,
                  duration: c.duration,
                  trimStart: c.trim_start ?? c.trimStart ?? 0,
                  trimEnd: c.trim_end ?? c.trimEnd ?? c.duration,
                  posterUrl: c.poster_url || c.posterUrl
                };
                setCurrentClip(clipObj);
                setCurrentTab('sync');
              }
            })
            .catch(() => {});
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleClipSelect = (clip: Clip | null) => {
    try {
      sessionStorage.setItem('soulsync_vault_open', 'false');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('soulsync:close_vault'));
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    } catch (_) {}
    setCurrentClip(clip);
    setActiveEntry(null);
    if (clip) {
      setCurrentTab('sync');
    }
  };

  const handleStartSeparation = (clip: Clip) => {
    setCurrentClip(clip);
    setIsSeparatorModalOpen(true);
  };

  const handleStemsReady = (newStems: StemSet) => {
    setStems(newStems);
    setIsSeparatorModalOpen(false);
    setCurrentTab('sync');
  };

  // Render Inspect page when at /inspect route
  if (isInspectMode) {
    return <Inspect />;
  }

  return (
    <TokenProvider userName={userName}>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header
        currentTab={currentTab}
        onTabChange={(tab) => {
          if (tab === 'sync' && currentTab === 'capture') {
            try {
              sessionStorage.setItem('soulsync_vault_open', 'false');
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('soulsync:close_vault'));
              }
            } catch (_) {}
          }
          setCurrentTab(tab);
        }}
        userName={userName}
        onUserLogin={handleUserLogin}
        currentMood={currentMood}
      />

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* ROOM 1: SoulCapture */}
        {currentTab === 'capture' && (
          <ClipCurator
            currentClip={currentClip}
            userName={userName}
            onClipSelect={handleClipSelect}
            onStartSeparation={handleStartSeparation}
            onSendToSplit={(clip) => {
              try {
                sessionStorage.setItem('soulsync_vault_open', 'false');
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('soulsync:close_vault'));
                  if (window.history && window.history.replaceState) {
                    window.history.replaceState({}, '', window.location.pathname);
                  }
                }
              } catch (_) {}
              setCurrentClip(clip);
              setActiveEntry(null);
              setCurrentTab('sync');
            }}
            onSendToShare={(clip) => {
              setPendingShareClip(clip);
              setCurrentClip(clip);
              setCurrentTab('share');
            }}
          />
        )}

        {/* ROOM 2: SoulSync Studio */}
        {currentTab === 'sync' && (
          <SoulStudio
            currentClip={currentClip}
            initialEntry={activeEntry}
            currentMood={currentMood}
            performerName={userName}
            onUpdatePerformerName={handleUserLogin}
            onSelectClip={handleClipSelect}
            onOpenGallery={() => setCurrentTab('share')}
            onBackToCapture={() => setCurrentTab('capture')}
            onEntryUpdated={(entry) => setActiveEntry(entry)}
            onShareCurrentClip={(clipToShare) => {
              setPendingShareClip(clipToShare);
              setCurrentTab('share');
            }}
          />
        )}

        {/* ROOM 3: SoulShare Hub */}
        {currentTab === 'share' && (
          <SoulShareGallery
            currentUserName={userName || ''}
            stagedClip={pendingShareClip}
            stagedEntry={activeEntry}
            onClearStagedClip={() => setPendingShareClip(null)}
            onOpenClipInStudio={(clipItem: SoulClipItem, mode: 'make_it_mine' | 'colab' = 'colab') => {
              const baseClipId = clipItem.parentClipId || clipItem.id.replace(/-shared-.*$/, '');
              const clipObj: Clip = {
                id: clipItem.id,
                parentClipId: baseClipId,
                title: clipItem.title,
                sourceUrl: clipItem.videoUrl,
                videoUrl: clipItem.videoUrl,
                audioUrl: clipItem.audioUrl,
                duration: clipItem.duration,
                trimStart: clipItem.trimStart,
                trimEnd: clipItem.trimEnd,
                posterUrl: clipItem.posterUrl,
                isPreset: clipItem.isPreset,
                isEditable: clipItem.isEditable
              };

              const partnerName = clipItem.creatorName || clipItem.userName || 'Dan';

              if (mode === 'make_it_mine') {
                // Rips all recorded takes for a completely clean personal canvas
                const freshEntry: SoulEntry = {
                  id: `entry-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`,
                  title: clipItem.title,
                  performerName: userName || 'Artist',
                  clipId: clipObj.id,
                  videoUrl: clipItem.videoUrl,
                  bgmUrl: clipItem.audioUrl || '',
                  vocalsUrl: (clipItem as any).vocalsUrl || '',
                  posterUrl: clipItem.posterUrl || '',
                  moodTag: currentMood,
                  trimStart: clipItem.trimStart || 0,
                  trimEnd: clipItem.trimEnd || clipItem.duration,
                  duration: clipItem.duration,
                  dialogueLines: (clipItem as any).dialogueLines || [],
                  takes: [],
                  takeCount: 0,
                  forkedFrom: clipItem.id,
                  forkDepth: 1,
                  status: 'draft',
                  partnerName,
                  createdAt: Date.now(),
                  updatedAt: Date.now()
                };
                setActiveEntry(freshEntry);
              } else {
                const rawTakes = Array.isArray((clipItem as any).takes) ? (clipItem as any).takes : (clipItem as any).activeTakes || [];
                const loadedTakes: EntryTake[] = rawTakes
                  .filter((t: any) => t && t.isActive !== false)
                  .map((t: any) => {
                    const isTakeByMe = Boolean(
                      userName &&
                      t.performerName &&
                      t.performerName.trim().toLowerCase() === userName.trim().toLowerCase()
                    );
                    return {
                      id: t.id || `take-${Math.random().toString(36).substring(2, 8)}`,
                      entryId: '',
                      takeNumber: t.takeNumber || 1,
                      performerName: t.performerName || (isTakeByMe ? userName : partnerName),
                      audioUrl: t.audioUrl || '',
                      duration: Number(t.duration) || 0,
                      lineIds: Array.isArray(t.lineIds) ? t.lineIds : [],
                      lineStart: t.lineStart ?? 0,
                      lineEnd: t.lineEnd ?? 0,
                      characterLabel: t.characterLabel || (isTakeByMe ? userName : partnerName),
                      volume: t.volume ?? 1.0,
                      createdAt: t.createdAt || Date.now(),
                      isActive: true,
                      isLocked: !isTakeByMe,        // 🔒 Locked in Yellow ONLY for partner's voice
                      isPartnerTake: !isTakeByMe
                    };
                  });

                const colabEntry: SoulEntry = {
                  id: `entry-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`,
                  title: clipItem.title,
                  performerName: userName || 'Artist',
                  clipId: clipObj.id,
                  videoUrl: clipItem.videoUrl,
                  bgmUrl: clipItem.audioUrl || '',
                  vocalsUrl: (clipItem as any).vocalsUrl || '',
                  posterUrl: clipItem.posterUrl || '',
                  moodTag: currentMood,
                  trimStart: clipItem.trimStart || 0,
                  trimEnd: clipItem.trimEnd || clipItem.duration,
                  duration: clipItem.duration,
                  dialogueLines: (clipItem as any).dialogueLines || [],
                  takes: loadedTakes,
                  takeCount: loadedTakes.length,
                  forkedFrom: clipItem.id,
                  forkDepth: 1,
                  colabSharedClipId: clipItem.id, // 👈 Tracks this shared_clip instance so sending back updates it!
                  partnerName,
                  status: 'draft',
                  createdAt: Date.now(),
                  updatedAt: Date.now()
                };
                setActiveEntry(colabEntry);
              }

              setCurrentClip(clipObj);
              setCurrentTab('sync');
            }}
            onNewClip={() => {
              setCurrentTab('capture');
            }}
          />
        )}
      </main>

      {/* Soul Separator AI Modal */}
      {currentClip && (
        <SoulSeparatorModal
          isOpen={isSeparatorModalOpen}
          onClose={() => setIsSeparatorModalOpen(false)}
          clip={currentClip}
          mood={currentMood}
          onStemsReady={handleStemsReady}
        />
      )}

      {/* Footer */}
      <footer
        style={{
          borderTop: '1px solid var(--border-glass)',
          padding: '1.25rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          fontSize: '0.8rem',
          color: 'var(--text-muted)',
          background: 'rgba(7, 8, 17, 0.9)'
        }}
      >
        <div>
          <span style={{ color: '#ffffff', fontWeight: 600 }}>SoulSync</span> • Collaborative Scene Dubbing & Vocal Performance
        </div>
        <div style={{ display: 'flex', gap: '1.25rem' }}>
          <span>Cloudflare Pages</span>
          <span>Workers & R2</span>
          <span>D1 Database</span>
        </div>
      </footer>
    </div>
  </TokenProvider>
);
};

export default App;
